import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  MessageSquare,
  CheckCircle,
} from "lucide-react";
import { useState, useRef, useEffect, type ChangeEvent, type FormEvent } from "react";
import DonationModal from "../components/common/DonationModal";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/contact`;
const backendUrl = "https://www.sevaafoundation.com/seva-backend";

const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;

const Contact = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
  const formSectionRef = useRef<HTMLDivElement>(null);
  
  // Naya State Contact Info ke liye
  const [contactInfo, setContactInfo] = useState({ phone: "+91-99102 18596", email: "info@sevaafoundation.com" });

  useEffect(() => {
    fetch("https://www.sevaafoundation.com/seva-backend/api/settings/get_contact_info.php")
      .then(res => res.json())
      .then(data => { if(data.success) setContactInfo(data.data); })
      .catch(err => console.error(err));
  }, []);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${backendUrl}/api/contact/submit.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setShowToast(true);
        setFormData({ name: "", email: "", subject: "", message: "" });
        setTimeout(() => setShowToast(false), 2000);
      } else {
        alert(`Error: ${result.message || "Failed to send message."}`);
      }
    } catch (error) {
      alert("Something went wrong. Please check your connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    formSectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  // Update address array to use dynamic state
  const contactDetails = [
    {
      icon: <MapPin aria-hidden="true" />,
      title: "Our Address",
      detail: "Noida, Uttar Pradesh,India 201301",
      color: "text-orange-500",
      bg: "bg-orange-50",
    },
    {
      icon: <Phone aria-hidden="true" />,
      title: "Call Us",
      detail: contactInfo.phone,
      href: `tel:${contactInfo.phone.replace(/[^0-9+]/g, '')}`,
      color: "text-teal-600",
      bg: "bg-teal-50",
    },
    {
      icon: <Mail aria-hidden="true" />,
      title: "Email Us",
      detail: contactInfo.email,
      href: `mailto:${contactInfo.email}`,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
  ];

  // Dynamic Schemas
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "Sevaa Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: { "@type": "ImageObject", url: logoImage },
    image: defaultImage,
    description: "Sevaa Foundation is a registered NGO in India working for child education, healthcare support, women empowerment, hunger relief and social welfare.",
    telephone: contactInfo.phone,
    email: contactInfo.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: "Noida, Uttar Pradesh,India",
      addressLocality: "New Delhi",
      postalCode: "110001",
      addressCountry: "IN",
    },
    areaServed: { "@type": "Country", name: "India" },
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: contactInfo.phone,
        contactType: "customer support",
        areaServed: "IN",
        availableLanguage: ["English", "Hindi"],
      },
      {
        "@type": "ContactPoint",
        email: contactInfo.email,
        contactType: "NGO support",
        areaServed: "IN",
        availableLanguage: ["English", "Hindi"],
      }
    ],
    sameAs: [siteUrl],
  };

  const contactPageSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${pageUrl}#contactpage`,
    name: "Contact Sevaa Foundation",
    url: pageUrl,
    inLanguage: "en-IN",
    description: "Contact Sevaa Foundation for donations, volunteering, membership, NGO support and social welfare initiatives in India.",
    isPartOf: { "@type": "WebSite", "@id": `${siteUrl}/#website`, name: "Sevaa Foundation", url: siteUrl },
    about: { "@id": `${siteUrl}/#ngo` },
    mainEntity: { "@id": `${siteUrl}/#ngo` },
    primaryImageOfPage: { "@type": "ImageObject", url: defaultImage, width: 1200, height: 630 },
    potentialAction: { "@type": "CommunicateAction", target: pageUrl, name: "Send a message to Sevaa Foundation" },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${pageUrl}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${siteUrl}/` },
      { "@type": "ListItem", position: 2, name: "Contact", item: pageUrl },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Contact Sevaa Foundation | NGO Helpline & Donation Support</title>
        <meta name="title" content="Contact Sevaa Foundation | NGO Helpline & Donation Support" />
        <meta name="description" content="Contact Sevaa Foundation for donations, volunteering, membership and NGO support. Reach our team for education, healthcare and social welfare initiatives." />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="Sevaa Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Sevaa Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Contact Sevaa Foundation | NGO Helpline & Donation Support" />
        <meta property="og:description" content="Reach Sevaa Foundation for donations, volunteering, membership and support for education, healthcare and social welfare initiatives." />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="Contact Sevaa Foundation for NGO donation and volunteer support" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Sevaa Foundation | NGO Support" />
        <meta name="twitter:description" content="Contact Sevaa Foundation for donations, volunteering, membership and social welfare support." />
        <meta name="twitter:image" content={defaultImage} />
        <meta name="twitter:image:alt" content="Contact Sevaa Foundation NGO support team" />
        <script type="application/ld+json">{JSON.stringify(contactPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main className="bg-white min-h-screen font-poppins pb-24 relative">
        <AnimatePresence>
          {showToast && (
            <motion.div initial={{ opacity: 0, y: -50, x: "-50%" }} animate={{ opacity: 1, y: 0, x: "-50%" }} exit={{ opacity: 0, y: -50, x: "-50%" }} role="status" aria-live="polite" className="fixed top-10 left-1/2 -translate-x-1/2 z-[100] bg-teal-900/95 backdrop-blur-sm border border-emerald-500/30 px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 min-w-[320px] max-w-md w-[90vw]">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
                <CheckCircle className="text-emerald-400" size={24} aria-hidden="true" />
              </div>
              <div>
                <h4 className="text-white font-bold text-sm">Message Sent!</h4>
                <p className="text-teal-100/70 text-xs mt-1">Thank you for reaching out. We&apos;ll reply soon.</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <section aria-labelledby="contact-hero-heading" className="relative h-[500px] flex items-center justify-center text-center text-white overflow-hidden">
          <div className="absolute inset-0 z-0 bg-cover bg-center transition-transform duration-1000 hover:scale-105" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&q=80')" }} aria-hidden="true">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"></div>
          </div>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 max-w-4xl px-6">
            <h1 id="contact-hero-heading" className="text-5xl md:text-7xl font-black mb-6 tracking-tight">Donate To <span className="text-orange-400">Make A Difference</span></h1>
            <p className="text-lg md:text-xl text-slate-200 mb-10 leading-relaxed font-medium">Hundreds of thousands of children experiencing or witnessing gender-based violence. Join our mission to provide safety, education, and a brighter future for those in need.</p>
          </motion.div>
        </section>

        <section aria-label="Donation and membership actions" className="max-w-7xl mx-auto px-6 -mt-20 relative z-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 shadow-2xl rounded-3xl overflow-hidden">
            <div className="bg-[#1D3D47] p-10 text-center text-white flex flex-col items-center group">
              <h3 className="text-2xl font-bold mb-4">Send Donation</h3>
              <p className="text-slate-300 mb-8 text-sm">Hundreds of thousands of children experiencing or witnessing violence.</p>
              <button onClick={scrollToForm} className="mt-auto bg-orange-500 hover:bg-orange-600 px-8 py-2 rounded font-bold transition-colors" aria-label="Go to contact form for donation support">↓</button>
            </div>
            <div className="bg-orange-500 p-10 text-center text-white flex flex-col items-center scale-105 z-10 shadow-xl">
              <h3 className="text-2xl font-bold mb-4">Become Member</h3>
              <p className="text-orange-50 mb-8 text-sm">Hundreds of thousands of children experiencing or witnessing violence.</p>
              <button onClick={scrollToForm} className="mt-auto bg-[#1D3D47] hover:bg-slate-900 px-8 py-2 rounded font-bold transition-colors" aria-label="Go to contact form to become a member">↓</button>
            </div>
            <div className="bg-[#1D3D47] p-10 text-center text-white flex flex-col items-center">
              <h3 className="text-2xl font-bold mb-4">Send Donation</h3>
              <p className="text-slate-300 mb-8 text-sm">Hundreds of thousands of children experiencing or witnessing violence.</p>
              <button onClick={scrollToForm} className="mt-auto bg-orange-500 hover:bg-orange-600 px-8 py-2 rounded font-bold transition-colors" aria-label="Go to contact form for donation help">↓</button>
            </div>
          </div>
        </section>

        <section aria-labelledby="contact-donation-heading" className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <h2 id="contact-donation-heading" className="text-4xl md:text-5xl font-black text-[#1D3D47] leading-tight">It&apos;s Easier To Take <br /> <span className="text-orange-500">Than To Give</span></h2>
              <div className="space-y-4">
                <p className="text-slate-600 leading-relaxed text-lg">Hundreds of thousands of children experiencing or witnessing and other gender-based violence. Sevaa Foundation has been the industry&apos;s standard when an unknown printer took a galley.</p>
                <p className="text-slate-500 italic border-l-4 border-orange-500 pl-4 py-2">&quot;Small acts, when multiplied by millions of people, can transform the world.&quot;</p>
              </div>
              <button onClick={() => setIsDonationModalOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-xl font-bold shadow-lg shadow-orange-200 transition-all" aria-label="Open donation form for Sevaa Foundation">Donate Now</button>
            </div>
            <div className="relative group">
              <div className="absolute -inset-4 bg-orange-100 rounded-[2rem] transition-transform"></div>
              <img src="https://www.smilefoundationindia.org/wp-content/uploads/2022/09/Banner1-scaled.jpg" alt="Sevaa Foundation social welfare and donation support for children" className="relative rounded-[2rem] shadow-2xl object-cover h-[450px] w-full" loading="lazy" />
            </div>
          </div>
        </section>

        <hr className="max-w-7xl mx-auto opacity-10 mb-24" />

        <section ref={formSectionRef} aria-labelledby="contact-form-heading" className="max-w-7xl mx-auto px-6 lg:px-8 scroll-mt-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} className="lg:col-span-5 space-y-10">
              <div>
                <span className="text-orange-600 font-bold uppercase tracking-widest text-sm">Reach Out</span>
                <h2 id="contact-form-heading" className="text-4xl font-black text-slate-900 mt-2 mb-6">Let&apos;s Connect and Discuss Our Vision</h2>
                <p className="text-slate-500 font-medium leading-relaxed">Have questions about our initiatives? We&apos;d love to hear from you.</p>
              </div>

              <address className="not-italic space-y-6">
                {contactDetails.map((item, idx) => (
                  <motion.div key={idx} variants={itemVariants} className="flex items-center gap-5 p-6 rounded-2xl border border-slate-100 hover:shadow-xl hover:shadow-slate-100 transition-all bg-white group">
                    <div className={`${item.bg} ${item.color} p-4 rounded-xl group-hover:scale-110 transition-transform`}>{item.icon}</div>
                    <div>
                      <h4 className="font-bold text-slate-900">{item.title}</h4>
                      {item.href ? (
                        <a href={item.href} className="text-slate-500 text-sm hover:text-[#0f766e] transition-colors">{item.detail}</a>
                      ) : (
                        <p className="text-slate-500 text-sm">{item.detail}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </address>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} className="lg:col-span-7 bg-teal-700 p-8 md:p-12 rounded-[3rem] border border-orange-100 shadow-sm relative">
              <h3 className="text-2xl font-black text-white mb-8 flex items-center gap-3"><MessageSquare className="text-orange-400" aria-hidden="true" />Send us a Message</h3>
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="contact-name" className="sr-only">Your Name</label>
                    <input id="contact-name" type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Your Name" autoComplete="name" required className="w-full p-4 rounded-xl bg-white border-none focus:ring-2 focus:ring-orange-500 outline-none shadow-sm" />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="sr-only">Email Address</label>
                    <input id="contact-email" type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Email Address" autoComplete="email" required className="w-full p-4 rounded-xl bg-white border-none focus:ring-2 focus:ring-orange-500 outline-none shadow-sm" />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="sr-only">Subject</label>
                  <input id="contact-subject" type="text" name="subject" value={formData.subject} onChange={handleInputChange} placeholder="Subject" autoComplete="off" required className="w-full p-4 rounded-xl bg-white border-none focus:ring-2 focus:ring-orange-500 outline-none shadow-sm" />
                </div>
                <div>
                  <label htmlFor="contact-message" className="sr-only">How can we help?</label>
                  <textarea id="contact-message" name="message" value={formData.message} onChange={handleInputChange} placeholder="How can we help?" rows={5} required className="w-full p-4 rounded-xl bg-white border-none focus:ring-2 focus:ring-orange-500 outline-none shadow-sm resize-none"></textarea>
                </div>
                <button type="submit" disabled={isSubmitting} className="w-full bg-[#1D3D47] hover:bg-slate-900 text-white font-black py-4 rounded-xl shadow-lg flex items-center justify-center gap-3 transition-all disabled:opacity-70" aria-label="Send message to Sevaa Foundation">
                  {isSubmitting ? "SENDING..." : <><Send size={18} className="text-orange-400" aria-hidden="true" />SEND MESSAGE</>}
                </button>
              </form>
            </motion.div>
          </div>
        </section>
        <DonationModal isOpen={isDonationModalOpen} onClose={() => setIsDonationModalOpen(false)} />
      </main>
    </>
  );
};

export default Contact;